// src/components/Organizer/SalesManagement.tsx
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import DateRangePicker from "./DateRangePicker";
import PayoutRequests from "./PayoutRequests";
import {
	EMPTY_SALES_SUMMARY,
	SaleTransaction,
	SalesQueryParams,
	SalesQueryPeriod,
	SalesSeriesInterval,
	SalesSeriesPoint,
	SalesSummary,
	fetchOrganizerSalesBundle,
} from "../../services/salesService";

const DEFAULT_RECENT_LIMIT = 10;
const REPORT_RECENT_LIMIT = 100;
const SALES_ERROR_MESSAGE = "売上データの取得に失敗しました";

const CHART_INTERVAL_OPTIONS: { value: SalesSeriesInterval; label: string }[] =
	[
		{ value: "monthly", label: "月別" },
		{ value: "daily", label: "日別" },
	];

const CHART_INTERVAL_LABELS: Record<
	SalesSeriesInterval,
	{ title: string; column: string }
> = {
	monthly: { title: "月別売上", column: "月" },
	daily: { title: "日別売上", column: "日付" },
};

const padZero = (value: number) => String(value).padStart(2, "0");

const formatDateInput = (value: string) => {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}
	return `${date.getFullYear()}-${padZero(date.getMonth() + 1)}-${padZero(
		date.getDate()
	)}`;
};

const escapeCsvValue = (value: string | number | null | undefined) => {
	if (value === null || value === undefined) {
		return "";
	}
	const stringValue =
		typeof value === "number" ? value.toString() : String(value ?? "");
	const sanitizedValue = stringValue.replace(/\r?\n/g, "\n");
	const needsQuotes = /[",\n]/.test(sanitizedValue);
	const escapedValue = sanitizedValue.replace(/"/g, '""');
	return needsQuotes ? `"${escapedValue}"` : escapedValue;
};

const formatCurrency = (value: number | null | undefined) => {
	const amount = Number.isFinite(value ?? NaN) ? Math.round(value ?? 0) : 0;
	return `¥${amount.toLocaleString("ja-JP")}`;
};

type SalesReportCsvContext = {
	periodDescription: string;
	customStartLabel?: string;
	customEndLabel?: string;
	trendTitle?: string;
	trendColumnLabel?: string;
};

const buildSalesReportCsv = (
	summary: SalesSummary,
	series: SalesSeriesPoint[],
	transactions: SaleTransaction[],
	context: SalesReportCsvContext
) => {
	const rows: string[][] = [];
	const addRow = (...cells: (string | number)[]) => {
		rows.push(cells.map((cell) => cell.toString()));
	};
	const addBlankRow = () => rows.push([""]);

	addRow("レポート生成日時", new Date().toLocaleString("ja-JP"));
	addRow("選択期間", context.periodDescription);
	if (context.customStartLabel) {
		addRow("開始日", context.customStartLabel);
	}
	if (context.customEndLabel) {
		addRow("終了日", context.customEndLabel);
	}

	addBlankRow();

	addRow("売上サマリー");
	addRow("項目", "値");
	addRow("総売上", formatCurrency(summary.totalSales));
	addRow("今月の売上", formatCurrency(summary.monthlySales));
	addRow("販売チケット数", `${summary.ticketsSold ?? 0}枚`);
	addRow("開催予定イベント", `${summary.upcomingEvents ?? 0}件`);
	addRow("プラットフォーム手数料", `-${formatCurrency(summary.platformFee)}`);
	addRow("決済手数料", `-${formatCurrency(summary.paymentFee)}`);
	addRow("入金予定金額", formatCurrency(summary.netIncome));

	addBlankRow();

	addRow(context.trendTitle ?? "売上推移");
	addRow(context.trendColumnLabel ?? "期間", "売上");
	if (series.length === 0) {
		addRow("データがありません", "-");
	} else {
		series.forEach((item) => {
			addRow(item.label || item.bucket, formatCurrency(item.sales));
		});
	}

	addBlankRow();

	addRow("最近の販売一覧");
	addRow("イベント", "日付", "チケット種別", "購入者", "枚数", "金額");
	if (transactions.length === 0) {
		addRow("データがありません", "-", "-", "-", "-", "-");
	} else {
		transactions.forEach((sale) => {
			addRow(
				sale.event_title,
				sale.date ?? "-",
				sale.ticket_type,
				sale.buyer_name,
				sale.quantity,
				formatCurrency(sale.amount)
			);
		});
	}

	return rows.map((row) => row.map(escapeCsvValue).join(",")).join("\n");
};

const createReportFileName = () => {
	const now = new Date();
	return `sales-report_${now.getFullYear()}${padZero(
		now.getMonth() + 1
	)}${padZero(now.getDate())}_${padZero(now.getHours())}${padZero(
		now.getMinutes()
	)}${padZero(now.getSeconds())}.csv`;
};

const triggerCsvDownload = (csvContent: string) => {
	if (typeof window === "undefined" || typeof document === "undefined") {
		return;
	}
	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", createReportFileName());
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
};

const SalesManagement: React.FC = () => {
	const [periodFilter, setPeriodFilter] =
		useState<SalesQueryPeriod>("this_month");
	const [customStartDate, setCustomStartDate] = useState<string>("");
	const [customEndDate, setCustomEndDate] = useState<string>("");
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isDownloading, setIsDownloading] = useState(false);
	const [chartInterval, setChartInterval] =
		useState<SalesSeriesInterval>("monthly");

	// Sales data state
	const [salesData, setSalesData] = useState<SalesSummary>({
		...EMPTY_SALES_SUMMARY,
	});

	const [recentSales, setRecentSales] = useState<SaleTransaction[]>([]);
	const [chartSeries, setChartSeries] = useState<SalesSeriesPoint[]>([]);
	const chartScrollRef = React.useRef<HTMLDivElement>(null);

	const formatDateForApi = (value: string): string | undefined => {
		if (!value) return undefined;
		return value.split("T")[0];
	};

	// 期間の日数を計算して、月別表示が有効かどうかを判定
	const isMonthlyAvailable = (): boolean => {
		switch (periodFilter) {
			case "all":
				return true; // 全期間は月別表示を許可
			case "this_month":
			case "last_month":
				return false; // 1ヶ月は日別のみ
			case "last_3_months":
			case "last_6_months":
			case "this_year":
				return true; // 2ヶ月以上
			case "custom": {
				if (!customStartDate || !customEndDate) {
					return true; // デフォルトで許可
				}
				const start = new Date(customStartDate);
				const end = new Date(customEndDate);
				const diffTime = Math.abs(end.getTime() - start.getTime());
				const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
				return diffDays >= 60; // 60日以上（約2ヶ月）
			}
			default:
				return true;
		}
	};

	const monthlyAvailable = isMonthlyAvailable();

	// 月別が利用できない場合は自動的に日別に設定
	React.useEffect(() => {
		if (!monthlyAvailable && chartInterval === "monthly") {
			setChartInterval("daily");
		}
	}, [monthlyAvailable, chartInterval]);

	// グラフを最新（右端）にスクロール
	React.useEffect(() => {
		if (chartScrollRef.current && chartSeries.length > 0) {
			const scrollContainer = chartScrollRef.current;
			// スクロールを右端に設定
			scrollContainer.scrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
		}
	}, [chartSeries]);

	const createQueryParams = useCallback(
		(limit = DEFAULT_RECENT_LIMIT): SalesQueryParams => {
			const params: SalesQueryParams = { limit, interval: chartInterval };

			if (periodFilter === "custom") {
				const start = formatDateForApi(customStartDate);
				const end = formatDateForApi(customEndDate);

				if (start) {
					params.startDate = start;
				}
				if (end) {
					params.endDate = end;
				}

				if (start || end) {
					params.period = "custom";
				}
			} else {
				params.period = periodFilter;
			}

			return params;
		},
		[periodFilter, customStartDate, customEndDate, chartInterval]
	);

	// 売上データの取得
	const fetchSalesData = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		try {
			const params = createQueryParams();
			const { summary, transactions, months } = await fetchOrganizerSalesBundle(
				params
			);
			setSalesData(summary);
			setRecentSales(transactions);
			setChartSeries(months);
		} catch (error) {
			console.error("売上データの取得に失敗しました:", error);
			setError(SALES_ERROR_MESSAGE);
			setSalesData({ ...EMPTY_SALES_SUMMARY });
			setRecentSales([]);
			setChartSeries([]);
			toast.error(SALES_ERROR_MESSAGE);
		} finally {
			setIsLoading(false);
		}
	}, [createQueryParams]);

	useEffect(() => {
		fetchSalesData();
	}, [fetchSalesData]);

	// レポートダウンロード
	const getPeriodDescription = () => {
		switch (periodFilter) {
			case "all":
				return "全期間";
			case "this_month":
				return "今月";
			case "last_month":
				return "先月";
			case "last_3_months":
				return "直近3ヶ月";
			case "last_6_months":
				return "直近6ヶ月";
			case "this_year":
				return "今年";
			case "custom": {
				const startLabel = customStartDate
					? formatDateInput(customStartDate)
					: "";
				const endLabel = customEndDate ? formatDateInput(customEndDate) : "";
				if (startLabel && endLabel) {
					return `期間指定 (${startLabel} 〜 ${endLabel})`;
				}
				return "期間指定";
			}
			default:
				return "未指定";
		}
	};

	const handleReportDownload = async () => {
		if (periodFilter === "custom" && (!customStartDate || !customEndDate)) {
			toast.error("開始日と終了日を入力してください");
			return;
		}

		if (isDownloading) {
			return;
		}

		try {
			setIsDownloading(true);
			const params = createQueryParams(REPORT_RECENT_LIMIT);
			const { summary, transactions, months } = await fetchOrganizerSalesBundle(
				params
			);
			const intervalLabels = CHART_INTERVAL_LABELS[chartInterval];

			const csvContent = buildSalesReportCsv(
				summary ?? salesData,
				months.length > 0 ? months : chartSeries,
				transactions.length > 0 ? transactions : recentSales,
				{
					periodDescription: getPeriodDescription(),
					customStartLabel: customStartDate
						? formatDateInput(customStartDate)
						: undefined,
					customEndLabel: customEndDate
						? formatDateInput(customEndDate)
						: undefined,
					trendTitle: intervalLabels.title,
					trendColumnLabel: intervalLabels.column,
				}
			);

			triggerCsvDownload(csvContent);
			toast.success("売上レポートをダウンロードしました");
		} catch (downloadError) {
			console.error("売上レポートのダウンロードに失敗しました:", downloadError);
			toast.error("売上レポートのダウンロードに失敗しました");
		} finally {
			setIsDownloading(false);
		}
	};

	const handleCustomStartDateChange = (value: string) => {
		setCustomStartDate(value);
		if (value && customEndDate && value > customEndDate) {
			setCustomEndDate("");
		}
	};

	const handleCustomEndDateChange = (value: string) => {
		if (value && customStartDate && value < customStartDate) {
			setCustomStartDate(value);
		}
		setCustomEndDate(value);
	};

	const handleResetCustomDates = () => {
		setCustomStartDate("");
		setCustomEndDate("");
	};


	// 保留中の売上カード用: ネット金額から逆算して手数料と総額を整合させる
	const combinedFeeRate = (salesData.platformFeeRate + salesData.paymentFeeRate) / 100;
	const pendingGross = combinedFeeRate < 1
		? Math.round(salesData.netIncome / (1 - combinedFeeRate))
		: 0;
	const pendingPlatformFee = Math.round(pendingGross * (salesData.platformFeeRate / 100));
	const pendingPaymentFee = Math.round(pendingGross * (salesData.paymentFeeRate / 100));

	const maxChartValue = chartSeries.reduce(
		(max, point) => (point.sales > max ? point.sales : max),
		0
	);

	// ローディング表示
	if (isLoading) {
		return (
			<div className="container mx-auto max-w-5xl p-6 min-h-screen">
				<div className="mb-8">
					<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
						売上管理
					</h2>
					<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
						売上データを読み込み中...
					</p>
				</div>
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500 mx-auto"></div>
					<p className="mt-4 text-gray-600 dark:text-gray-400">読み込み中...</p>
				</div>
			</div>
		);
	}

	// エラー表示
	if (error) {
		return (
			<div className="container mx-auto max-w-5xl p-6 min-h-screen">
				<div className="mb-8">
					<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
						売上管理
					</h2>
				</div>
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
					<p className="text-red-600 dark:text-red-400">{error}</p>
					<button
						onClick={() => window.location.reload()}
						className="mt-4 px-6 py-2 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-lg font-medium transition-all duration-200"
					>
						再読み込み
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="container mx-auto max-w-5xl p-6 min-h-screen">
			<div className="mb-8">
				<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
					<div>
						<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
							売上管理
						</h2>
						<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
							売上データと収支の確認
						</p>
					</div>
					<div className="flex items-center gap-3">
						<span className="text-sm text-gray-600 dark:text-gray-400">
							期間:
						</span>
						<select
							value={periodFilter}
							onChange={(e) =>
								setPeriodFilter(e.target.value as SalesQueryPeriod)
							}
							className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
						>
							<option value="all">全期間</option>
							<option value="this_month">今月</option>
							<option value="last_month">先月</option>
							<option value="last_3_months">直近3ヶ月</option>
							<option value="last_6_months">直近6ヶ月</option>
							<option value="this_year">今年</option>
							<option value="custom">期間指定</option>
						</select>
					</div>
				</div>
				{periodFilter === "custom" && (
					<DateRangePicker
						startDate={customStartDate}
						endDate={customEndDate}
						onStartChange={handleCustomStartDateChange}
						onEndChange={handleCustomEndDateChange}
						onReset={handleResetCustomDates}
						idPrefix="sales-period"
					/>
				)}
			</div>

			<div className="space-y-6">
				{/* 売上サマリーカード */}
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
					{[
						{
							label: "総売上",
							value: `¥${salesData.totalSales.toLocaleString()}`,
							icon: (
								<svg
									className="w-8 h-8 text-violet-500"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							),
						},
						{
							label: "今月の売上",
							value: `¥${salesData.monthlySales.toLocaleString()}`,
							icon: (
								<svg
									className="w-8 h-8 text-blue-500"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
									/>
								</svg>
							),
						},
						{
							label: "販売チケット数",
							value: `${salesData.ticketsSold}枚`,
							icon: (
								<svg
									className="w-8 h-8 text-green-500"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
									/>
								</svg>
							),
						},
						{
							label: "開催予定イベント",
							value: `${salesData.upcomingEvents}件`,
							icon: (
								<svg
									className="w-8 h-8 text-orange-500"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
									/>
								</svg>
							),
						},
					].map((item, index) => (
						<div
							key={index}
							className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
						>
							<div className="flex items-start justify-between mb-2">
								<h3 className="text-sm text-gray-600 dark:text-gray-400">
									{item.label}
								</h3>
								{item.icon}
							</div>
							<p className="text-2xl font-bold text-gray-900 dark:text-white">
								{item.value}
							</p>
						</div>
					))}
				</div>

				{/* 売上推移グラフ */}
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
					<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
						<div>
							<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
								売上推移
							</h3>
							<p className="text-sm text-gray-600 dark:text-gray-400">
								{CHART_INTERVAL_LABELS[chartInterval].title}を確認
							</p>
						</div>
						{monthlyAvailable && (
							<div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-1">
								{CHART_INTERVAL_OPTIONS.map((option) => (
									<button
										key={option.value}
										type="button"
										onClick={() => setChartInterval(option.value)}
										aria-pressed={chartInterval === option.value}
										className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
											chartInterval === option.value
												? "bg-white dark:bg-gray-800 text-violet-600 dark:text-violet-300 shadow"
												: "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
										}`}
									>
										{option.label}
									</button>
								))}
							</div>
						)}
					</div>
					{chartSeries.length === 0 ? (
						<div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400">
							表示できる売上データがありません
						</div>
					) : (
						<div
							ref={chartScrollRef}
							className="h-64 overflow-x-auto pb-2 sales-chart-scroll"
							style={{
								scrollbarWidth: 'thin',
								scrollbarColor: '#8b5cf6 #e5e7eb'
							}}
						>
							<style dangerouslySetInnerHTML={{__html: `
								.sales-chart-scroll::-webkit-scrollbar {
									height: 8px;
								}
								.sales-chart-scroll::-webkit-scrollbar-track {
									background: #e5e7eb;
									border-radius: 4px;
								}
								.dark .sales-chart-scroll::-webkit-scrollbar-track {
									background: #374151;
								}
								.sales-chart-scroll::-webkit-scrollbar-thumb {
									background: linear-gradient(to right, #8b5cf6, #a855f7);
									border-radius: 4px;
								}
								.sales-chart-scroll::-webkit-scrollbar-thumb:hover {
									background: linear-gradient(to right, #7c3aed, #9333ea);
								}
								.dark .sales-chart-scroll {
									scrollbar-color: #8b5cf6 #374151;
								}
							`}} />
							<div
								className="flex h-full items-end gap-2 px-1"
								style={{
									minWidth: `${
										chartSeries.length * 60 + (chartSeries.length - 1) * 16
									}px`,
								}}
							>
								{chartSeries.map((data, index) => {
									const heightPx =
										maxChartValue > 0
											? Math.max(6, (data.sales / maxChartValue) * 200)
											: 6;
									return (
										<div
											key={`${data.bucket}-${index}`}
											className="flex flex-col items-center"
											style={{ flex: `1 1 40px`, minWidth: "40px" }}
										>
											<div className="w-full flex flex-col items-center">
												<div
													className={`w-full transition-all duration-300 ${
														data.sales > 0
															? "bg-gradient-to-t from-violet-500 to-purple-600"
															: "bg-gray-300 dark:bg-gray-600"
													}`}
													style={{ height: `${heightPx}px` }}
												></div>
												<div className="mt-2 text-xs text-gray-600 dark:text-gray-400 text-center whitespace-nowrap">
													{data.label || data.bucket}
												</div>
												<div className="text-sm font-bold text-gray-900 dark:text-white">
													{formatCurrency(data.sales)}
												</div>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					)}
				</div>

				{/* 保留中の売上と入金申請履歴 */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					{/* 保留中の売上 */}
					<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
						<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
							保留中の売上
						</h3>
						<div className="space-y-3">
							<div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-2">
								<span className="text-gray-600 dark:text-gray-400">総売上</span>
								<span className="font-bold text-gray-900 dark:text-white">
									¥{pendingGross.toLocaleString()}
								</span>
							</div>
							<div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-2">
								<span className="text-red-600 dark:text-red-400">
									プラットフォーム手数料 ({salesData.platformFeeRate}%)
								</span>
								<span className="text-red-600 dark:text-red-400">
									-¥{pendingPlatformFee.toLocaleString()}
								</span>
							</div>
							<div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-2">
								<span className="text-red-600 dark:text-red-400">
									決済手数料 ({salesData.paymentFeeRate}%)
								</span>
								<span className="text-red-600 dark:text-red-400">
									-¥{pendingPaymentFee.toLocaleString()}
								</span>
							</div>
							<div className="flex justify-between items-center pt-2">
								<span className="font-semibold text-gray-900 dark:text-white">
									入金予定金額
								</span>
								<span className="text-lg font-bold text-violet-600 dark:text-violet-400">
									¥{salesData.netIncome.toLocaleString()}
								</span>
							</div>
						</div>
					</div>

					{/* 入金申請履歴 */}
					<PayoutRequests
						availableAmount={salesData.netIncome}
						onPayoutCompleted={fetchSalesData}
					/>
				</div>

				{/* 最近の販売テーブル */}
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
					<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
						最近の販売
					</h3>
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead>
								<tr className="text-left border-b border-gray-200 dark:border-gray-700">
									<th className="pb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
										イベント
									</th>
									<th className="pb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
										日付
									</th>
									<th className="pb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
										種別
									</th>
									<th className="pb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
										購入者
									</th>
									<th className="pb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
										枚数
									</th>
									<th className="pb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
										金額
									</th>
								</tr>
							</thead>
							<tbody>
								{recentSales.length === 0 ? (
									<tr>
										<td
											colSpan={6}
											className="py-8 text-center text-gray-500 dark:text-gray-400"
										>
											販売データがありません
										</td>
									</tr>
								) : (
									recentSales.map((sale) => (
										<tr
											key={sale.id}
											className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
										>
											<td className="py-3 text-gray-900 dark:text-white">
												{sale.event_title}
											</td>
											<td className="py-3 text-gray-600 dark:text-gray-400">
												{sale.date}
											</td>
											<td className="py-3 text-gray-600 dark:text-gray-400">
												{sale.ticket_type}
											</td>
											<td className="py-3 text-gray-600 dark:text-gray-400">
												{sale.buyer_name}
											</td>
											<td className="py-3 text-gray-600 dark:text-gray-400">
												{sale.quantity}枚
											</td>
											<td className="py-3 font-medium text-gray-900 dark:text-white">
												¥{Math.round(sale.amount).toLocaleString()}
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
					<div className="mt-6 flex justify-end">
						<button
							onClick={handleReportDownload}
							disabled={isDownloading}
							className={`px-6 py-2 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-lg font-medium transition-all duration-200 ${
								isDownloading
									? "opacity-60 cursor-not-allowed"
									: "shadow-sm hover:shadow-md"
							}`}
						>
							{isDownloading
								? "レポート生成中..."
								: "売上レポートをダウンロード"}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SalesManagement;
